import { emailMatchesDomain, isCorporateDomain } from 'shared';
import { z } from 'zod';

import { SubscriptionType } from '../../../types/plan.types';

export const parseRecipients = (raw?: string) =>
  (raw ?? '')
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Stripe rejects a price `unit_amount` above 99,999,999 cents ($999,999.99). Prices here are in
// major units (dollars), so the cap is 999,999.99.
const MAX_PRICE = 999_999.99;

export const createOfferSchema = z
  .object({
    companyName: z.string().trim().min(1, 'Company name is required'),
    companyDomain: z
      .string()
      .trim()
      .min(1, 'Company domain is required')
      .refine(isCorporateDomain, 'Please enter a valid company domain'),
    planType: z.enum(SubscriptionType),
    currency: z.string().default('USD'),
    // Optional in the field so the plan-sync effects can clear it (like the price fields);
    // requiredness is enforced for every plan type in superRefine below.
    stakeholderLimit: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
      z
        .number({ message: 'Please enter a numeric value' })
        .int('Must be a whole number')
        .positive('Stakeholder limit must be greater than 0')
        .optional(),
    ),
    docusignEnabled: z.boolean().default(false),
    docusignEnvelopeLimit: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
      z.number().int('Must be a whole number').positive('Must be at least 1').max(10000).optional(),
    ),
    recipients: z.string().optional().default(''),
    monthlyEnabled: z.boolean().default(false),
    monthlyPrice: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
      z
        .number()
        .positive('Enter a valid amount')
        .max(MAX_PRICE, 'Price cannot exceed 999,999.99')
        .optional(),
    ),
    yearlyPrice: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
      z
        .number()
        .positive('Enter a valid amount')
        .max(MAX_PRICE, 'Price cannot exceed 999,999.99')
        .optional(),
    ),
    promoCode: z.string().optional().default(''),
  })
  .superRefine((data, ctx) => {
    const recipients = parseRecipients(data.recipients);

    if (recipients.length > 3) {
      ctx.addIssue({
        code: 'custom',
        path: ['recipients'],
        message: 'Up to 3 recipients are allowed',
      });
    }

    for (const email of recipients) {
      if (!EMAIL_PATTERN.test(email)) {
        ctx.addIssue({ code: 'custom', path: ['recipients'], message: `Invalid email: ${email}` });
        break;
      }
      if (data.companyDomain && !emailMatchesDomain(email, data.companyDomain)) {
        ctx.addIssue({
          code: 'custom',
          path: ['recipients'],
          message: 'Recipient email addresses must match the company domain',
        });
        break;
      }
    }

    if (data.stakeholderLimit == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['stakeholderLimit'],
        message: 'Please enter a numeric value',
      });
    }

    if (data.docusignEnabled && !data.docusignEnvelopeLimit) {
      ctx.addIssue({
        code: 'custom',
        path: ['docusignEnvelopeLimit'],
        message: 'Enter an envelope limit',
      });
    }

    if (data.planType === SubscriptionType.ENTERPRISE) {
      if (!data.yearlyPrice) {
        ctx.addIssue({
          code: 'custom',
          path: ['yearlyPrice'],
          message: 'Yearly price is required',
        });
      }
      if (data.monthlyEnabled && !data.monthlyPrice) {
        ctx.addIssue({
          code: 'custom',
          path: ['monthlyPrice'],
          message: 'Monthly price is required',
        });
      }
    }
  });

export type CreateOfferFormSchema = z.infer<typeof createOfferSchema>;
